// src/components/ManageExpensesAndDeposites.jsx
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  getAllTransactions,
  getAllTransactionsWithDeleted,
  getDeletedTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  deleteMultipleTransactions,
  restoreTransaction,
  restoreMultipleTransactions,
  getStatistics,
  exportReport
} from './Service';

export const ManageExpensesAndDeposites = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    type: '',
    category: '',
    paymentmethod: ''
  });
  const [summary, setSummary] = useState({
    totalExpenses: 0,
    totalDeposits: 0,
    balance: 0,
    deletedCount: 0
  });
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });
  
  const [formData, setFormData] = useState({
    description: '',
    type: 'Expense',
    money: '',
    date: new Date().toISOString().split('T')[0],
    categoury: 'Other',
    paymentmethod: 'Cash'
  });

  useEffect(() => {
    if (activeTab === 'active') {
      fetchActiveTransactions();
    } else if (activeTab === 'deleted') {
      fetchDeletedTransactions();
    } else {
      fetchAllTransactions();
    }
  }, [filters, pagination.currentPage, pagination.limit, activeTab]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    setSelectAll(selectedItems.length === transactions.length && transactions.length > 0);
  }, [selectedItems, transactions]);

  const fetchActiveTransactions = async () => {
    setLoading(true);
    try {
      const response = await getAllTransactions({
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit,
      });
      
      setTransactions(response.data || []);
      if (response.summary) {
        setSummary(response.summary);
      }
      if (response.pagination) {
        setPagination({
          currentPage: response.pagination.currentPage || 1,
          totalPages: response.pagination.NumberOfPages || 1,
          totalCount: response.pagination.totalItems || 0,
          limit: response.pagination.limit || 10
        });
      }
      setSelectedItems([]);
    } catch (error) {
      console.error('Error:', error);
      alert('حدث خطأ في جلب المعاملات النشطة');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedTransactions = async () => {
    setLoading(true);
    try {
      const response = await getDeletedTransactions({
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit,
      });
      setTransactions(response.data || []);
      if (response.summary) {
        setSummary(response.summary);
      }
      if (response.pagination) {
        setPagination({
          currentPage: response.pagination.currentPage || 1,
          totalPages: response.pagination.NumberOfPages || 1,
          totalCount: response.pagination.totalItems || 0,
          limit: response.pagination.limit || 10
        });
      }
      setSelectedItems([]);
    } catch (error) {
      console.error('Error:', error);
      alert('حدث خطأ في جلب المعاملات المحذوفة');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTransactions = async () => {
    setLoading(true);
    try {
      const response = await getAllTransactionsWithDeleted({
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit,
      });
      setTransactions(response.data || []);
      if (response.summary) {
        setSummary(response.summary);
      }
      if (response.pagination) {
        setPagination({
          currentPage: response.pagination.currentPage || 1,
          totalPages: response.pagination.NumberOfPages || 1,
          totalCount: response.pagination.totalItems || 0,
          limit: response.pagination.limit || 10
        });
      }
      setSelectedItems([]);
    } catch (error) {
      console.error('Error:', error);
      alert('حدث خطأ في جلب جميع المعاملات');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await getStatistics();
      if (response.success && response.data) {
        console.log('Statistics:', response.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.money || !formData.date) {
      alert('يرجى إدخال المبلغ والتاريخ');
      return;
    }
    
    setLoading(true);
    try {
      if (editingItem) {
        await updateTransaction(editingItem._id, formData);
        alert('تم التحديث بنجاح');
      } else {
        await createTransaction(formData);
        alert('تمت الإضافة بنجاح');
      }
      setShowModal(false);
      resetForm();
      if (activeTab === 'active') {
        fetchActiveTransactions();
      } else if (activeTab === 'deleted') {
        fetchDeletedTransactions();
      } else {
        fetchAllTransactions();
      }
      fetchStatistics();
    } catch (error) {
      console.error('Error:', error);
      alert('حدث خطأ في حفظ البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذه المعاملة؟')) {
      setLoading(true);
      try {
        await deleteTransaction(id);
        alert('تم الحذف بنجاح');
        if (activeTab === 'active') {
          fetchActiveTransactions();
        } else if (activeTab === 'deleted') {
          fetchDeletedTransactions();
        } else {
          fetchAllTransactions();
        }
        fetchStatistics();
      } catch (error) {
        console.error('Error:', error);
        alert('حدث خطأ في الحذف');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm('هل أنت متأكد من استعادة هذه المعاملة؟')) {
      setLoading(true);
      try {
        await restoreTransaction(id);
        alert('تم استعادة المعاملة بنجاح');
        if (activeTab === 'active') {
          fetchActiveTransactions();
        } else if (activeTab === 'deleted') {
          fetchDeletedTransactions();
        } else {
          fetchAllTransactions();
        }
        fetchStatistics();
      } catch (error) {
        console.error('Error:', error);
        alert('حدث خطأ في استعادة المعاملة');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedItems.length === 0) {
      alert('الرجاء تحديد المعاملات المراد حذفها');
      return;
    }
    
    if (window.confirm(`هل أنت متأكد من حذف ${selectedItems.length} معاملة؟`)) {
      setLoading(true);
      try {
        await deleteMultipleTransactions(selectedItems);
        alert(`تم حذف ${selectedItems.length} معاملة بنجاح`);
        if (activeTab === 'active') {
          fetchActiveTransactions();
        } else if (activeTab === 'deleted') {
          fetchDeletedTransactions();
        } else {
          fetchAllTransactions();
        }
        fetchStatistics();
      } catch (error) {
        console.error('Error:', error);
        alert('حدث خطأ في حذف المعاملات');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRestoreMultiple = async () => {
    if (selectedItems.length === 0) {
      alert('الرجاء تحديد المعاملات المراد استعادتها');
      return;
    }
    
    if (window.confirm(`هل أنت متأكد من استعادة ${selectedItems.length} معاملة؟`)) {
      setLoading(true);
      try {
        await restoreMultipleTransactions(selectedItems);
        alert(`تم استعادة ${selectedItems.length} معاملة بنجاح`);
        if (activeTab === 'active') {
          fetchActiveTransactions();
        } else if (activeTab === 'deleted') {
          fetchDeletedTransactions();
        } else {
          fetchAllTransactions();
        }
        fetchStatistics();
      } catch (error) {
        console.error('Error:', error);
        alert('حدث خطأ في استعادة المعاملات');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(transactions.map(t => t._id));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      description: item.description || '',
      type: item.type,
      money: item.money,
      date: item.date,
      categoury: item.categoury,
      paymentmethod: item.paymentmethod
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      description: '',
      type: 'Expense',
      money: '',
      date: new Date().toISOString().split('T')[0],
      categoury: 'Other',
      paymentmethod: 'Cash'
    });
    setEditingItem(null);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      from: '',
      to: '',
      type: '',
      category: '',
      paymentmethod: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ 
      ...prev, 
      limit: newLimit,
      currentPage: 1 
    }));
  };

  const exportToExcel = async () => {
    try {
      let status = '';
      if (activeTab === 'active') status = 'active';
      else if (activeTab === 'deleted') status = 'deleted';
      
      const response = await exportReport({ ...filters, status, limit: 1000 });
      
      if (!response.data || response.data.length === 0) {
        alert('لا توجد بيانات للتصدير');
        return;
      }
      
      const exportData = response.data.map((item, index) => ({
        'م': index + 1,
        'التاريخ': item['التاريخ'],
        'الوصف': item['الوصف'],
        'النوع': item['النوع'],
        'الفئة': item['الفئة'],
        'طريقة الدفع': item['طريقة الدفع'],
        'المبلغ (درهم)': item['المبلغ'],
        'الحالة': item['الحالة'] || (activeTab === 'active' ? 'نشط' : activeTab === 'deleted' ? 'محذوف' : '')
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'المعاملات');
      
      worksheet['!cols'] = [
        { wch: 5 }, { wch: 12 }, { wch: 30 }, { wch: 10 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }
      ];
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `transactions_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
      
      alert('تم تصدير التقرير بنجاح');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('حدث خطأ في تصدير التقرير');
    }
  };

  const getTypeBadge = (type) => {
    if (type === 'Expense') {
      return <span className="badge badge-expense">📉 مصروف</span>;
    } else if (type === 'Deposite') {
      return <span className="badge badge-deposit">📈 إيداع</span>;
    }
    return <span className="badge badge-other">📌 أخرى</span>;
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return <span className="badge badge-active">✅ نشط</span>;
    } else {
      return <span className="badge badge-deleted">🗑️ محذوف</span>;
    }
  };

  // ========== التعديل الأول: إضافة الأيقونات للفئات الجديدة ==========
  const getCategoryIcon = (category) => {
    const icons = {
      'Rent': '🏠', 
      'Billing': '💡', 
      'Supplies': '📦',
      'Services': '⚙️', 
      'Traning': '📚', 
      'Travilling': '✈️',
      'Markiting': '📢', 
      // الفئات الجديدة المطلوبة
      'salary': '💰',      // رواتب
      'accoumiditio': '🪪', // اقامات
      'licience': '📜',     // رخصة
      'Other': '📌'
    };
    return icons[category] || '📌';
  };

  const getPaymentIcon = (method) => {
    const icons = {
      'Cash': '💵', 'Bank Transfer': '🏦',
      'Credit Card': '💳', 'Check': '📝', 'Other': '🔧'
    };
    return icons[method] || '💰';
  };

  return (
    <div className="manage-expenses-container" dir="rtl">
      <div className="header">
        <div className="header-content">
          <h1>📊 Rukn Almrooj Accounting</h1>
        </div>
      </div>

      <div className="summary-cards">
        <div className="card expense-card">
          <div className="card-icon">📉</div>
          <div className="card-content">
            <h3>إجمالي المصروفات</h3>
            <p className="amount">{summary.totalExpenses?.toLocaleString()} <span>درهم</span></p>
          </div>
        </div>
        <div className="card deposit-card">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <h3>إجمالي الإيداعات</h3>
            <p className="amount">{summary.totalDeposits?.toLocaleString()} <span>درهم</span></p>
          </div>
        </div>
        <div className="card balance-card">
          <div className="card-icon">⚖️</div>
          <div className="card-content">
            <h3>صافي الرصيد</h3>
            <p className={`amount ${summary.balance >= 0 ? 'positive' : 'negative'}`}>
              {summary.balance?.toLocaleString()} <span>درهم</span>
            </p>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <button className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')}>📋 المعاملات النشطة</button>
        <button className={`tab-btn ${activeTab === 'deleted' ? 'active' : ''}`} onClick={() => setActiveTab('deleted')}>🗑️ المعاملات المحذوفة</button>
        <button className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>📊 جميع المعاملات</button>
      </div>

      <div className="filters-section">
        <h3>🔍 فلاتر البحث المتقدمة</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>📅 من تاريخ</label>
            <input type="date" name="from" value={filters.from} onChange={handleFilterChange} />
          </div>
          <div className="filter-group">
            <label>📅 إلى تاريخ</label>
            <input type="date" name="to" value={filters.to} onChange={handleFilterChange} />
          </div>
          <div className="filter-group">
            <label>💰 النوع</label>
            <select name="type" value={filters.type} onChange={handleFilterChange}>
              <option value="">🔍 الكل</option>
              <option value="Expense">📉 مصروف</option>
              <option value="Deposite">📈 إيداع</option>
            </select>
          </div>
          <div className="filter-group">
            <label>🏷️ الفئة</label>
            <select name="category" value={filters.category} onChange={handleFilterChange}>
              <option value="">🔍 الكل</option>
              <option value="Rent">🏠 إيجار</option>
              <option value="Billing">💡 فواتير</option>
              {/* ========== التعديل الثاني: إضافة الفئات الجديدة في الفلاتر ========== */}
              <option value="salary">💰 رواتب</option>
              <option value="accoumiditio">🪪 اقامات</option>
              <option value="licience">📜 رخصة</option>
              <option value="Supplies">📦 مستلزمات</option>
              <option value="Services">⚙️ خدمات</option>
              <option value="Traning">📚 تدريب</option>
              <option value="Travilling">✈️ سفر</option>
              <option value="Markiting">📢 تسويق</option>
              <option value="Other">📌 أخرى</option>
            </select>
          </div>
          <div className="filter-group">
            <label>💳 طريقة الدفع</label>
            <select name="paymentmethod" value={filters.paymentmethod} onChange={handleFilterChange}>
              <option value="">🔍 الكل</option>
              <option value="Cash">💵 كاش</option>
              <option value="Bank Transfer">🏦 تحويل بنكي</option>
              <option value="Credit Card">💳 بطاقة ائتمان</option>
              <option value="Check">📝 شيك</option>
              <option value="Other">🔧 أخرى</option>
            </select>
          </div>
          <div className="filter-buttons">
            <button onClick={clearFilters} className="btn-clear">🗑️ مسح الفلاتر</button>
            <button onClick={exportToExcel} className="btn-export">📥 تصدير Excel</button>
          </div>
        </div>
      </div>

      <div className="action-bar">
        <div className="action-left">
          {selectedItems.length > 0 && activeTab === 'active' && (
            <button onClick={handleDeleteMultiple} className="btn-delete-multiple">🗑️ حذف المحدد ({selectedItems.length})</button>
          )}
          {selectedItems.length > 0 && activeTab === 'deleted' && (
            <button onClick={handleRestoreMultiple} className="btn-restore-multiple">↩️ استعادة المحدد ({selectedItems.length})</button>
          )}
          {selectedItems.length > 0 && activeTab === 'all' && (
            <div className="multi-actions">
              <button onClick={handleDeleteMultiple} className="btn-delete-multiple">🗑️ حذف المحدد ({selectedItems.length})</button>
              <button onClick={handleRestoreMultiple} className="btn-restore-multiple">↩️ استعادة المحدد ({selectedItems.length})</button>
            </div>
          )}
        </div>
        <div className="action-right">
          <div className="pagination-top-wrapper">
            {pagination.totalPages >= 1 && (
              <div className="pagination-top">
                <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} className="pagination-btn-top" title="السابق">‹</button>
                <span className="pagination-info-top">{pagination.currentPage} / {pagination.totalPages}</span>
                <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages} className="pagination-btn-top" title="التالي">›</button>
              </div>
            )}
            <div className="limit-selector">
              <label>عرض: </label>
              <select value={pagination.limit} onChange={handleLimitChange}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            {activeTab === 'active' && (
              <button onClick={() => setShowModal(true)} className="btn-add">➕ إضافة معاملة جديدة</button>
            )}
          </div>
        </div>
      </div>

      {/* ========== الجدول للشاشات الكبيرة ========== */}
      <div className="table-container desktop-view">
        {loading ? (
          <div className="loading"><div className="spinner"></div><p>جاري التحميل...</p></div>
        ) : (
          <table className="transactions-table">
            <thead>
              <tr>
                <th className="checkbox-col"><input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="checkbox" /></th>
                <th>#</th><th>التاريخ</th><th>الوصف</th><th>النوع</th><th>الفئة</th><th>طريقة الدفع</th><th>المبلغ</th>
                {activeTab === 'all' && <th>الحالة</th>}<th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr className="empty-row"><td colSpan={activeTab === 'all' ? 10 : 9}><div className="empty-state"><span className="empty-icon">📭</span><p>{activeTab === 'active' ? 'لا توجد معاملات نشطة' : activeTab === 'deleted' ? 'لا توجد معاملات محذوفة' : 'لا توجد معاملات'}</p>{activeTab === 'active' && <button onClick={() => setShowModal(true)} className="btn-add-small">أضف أول معاملة</button>}</div></td></tr>
              ) : (
                transactions.map((item, index) => (
                  <tr key={item._id} className={item.type === 'Expense' ? 'expense-row' : 'deposit-row'}>
                    <td className="checkbox-col"><input type="checkbox" checked={selectedItems.includes(item._id)} onChange={() => handleSelectItem(item._id)} className="checkbox" /></td>
                    <td>{(pagination.currentPage - 1) * pagination.limit + index + 1}</td>
                    <td>{item.date}</td>
                    <td className="description-cell">{item.description || '-'}</td>
                    <td>{getTypeBadge(item.type)}</td>
                    <td><span className="category-badge">{getCategoryIcon(item.categoury)} {item.categoury}</span></td>
                    <td><span className="payment-badge">{getPaymentIcon(item.paymentmethod)} {item.paymentmethod}</span></td>
                    <td className={item.type === 'Expense' ? 'expense-amount' : 'deposit-amount'}>{item.money.toLocaleString()} درهم</td>
                    {activeTab === 'all' && <td>{getStatusBadge(item.isActive)}</td>}
                    <td className="actions-cell">
                      {activeTab === 'active' && (<><button onClick={() => handleEdit(item)} className="btn-edit" title="تعديل">✏️</button><button onClick={() => handleDelete(item._id)} className="btn-delete" title="حذف">🗑️</button></>)}
                      {activeTab === 'deleted' && (<button onClick={() => handleRestore(item._id)} className="btn-restore" title="استعادة">↩️ استعادة</button>)}
                      {activeTab === 'all' && (<div className="all-actions">{item.isActive ? <button onClick={() => handleDelete(item._id)} className="btn-delete" title="حذف">🗑️</button> : <button onClick={() => handleRestore(item._id)} className="btn-restore" title="استعادة">↩️</button>}<button onClick={() => handleEdit(item)} className="btn-edit" title="تعديل">✏️</button></div>)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ========== عرض الكروت للشاشات الصغيرة (Mobile & Tablet) ========== */}
      <div className="cards-container mobile-view">
        {loading ? (
          <div className="loading"><div className="spinner"></div><p>جاري التحميل...</p></div>
        ) : transactions.length === 0 ? (
          <div className="empty-state"><span className="empty-icon">📭</span><p>{activeTab === 'active' ? 'لا توجد معاملات نشطة' : activeTab === 'deleted' ? 'لا توجد معاملات محذوفة' : 'لا توجد معاملات'}</p>{activeTab === 'active' && <button onClick={() => setShowModal(true)} className="btn-add-small">أضف أول معاملة</button>}</div>
        ) : (
          transactions.map((item, index) => (
            <div key={item._id} className={`transaction-card ${item.type === 'Expense' ? 'card-expense' : 'card-deposit'}`}>
              <div className="card-header">
                <div className="card-checkbox">
                  <input type="checkbox" checked={selectedItems.includes(item._id)} onChange={() => handleSelectItem(item._id)} className="checkbox-mobile" />
                  <span className="card-number">#{(pagination.currentPage - 1) * pagination.limit + index + 1}</span>
                </div>
                <div className="card-badges">
                  {getTypeBadge(item.type)}
                  {activeTab === 'all' && getStatusBadge(item.isActive)}
                </div>
              </div>
              <div className="card-body">
                <div className="card-row"><span className="card-label">📅 التاريخ:</span><span className="card-value">{item.date}</span></div>
                <div className="card-row"><span className="card-label">📝 الوصف:</span><span className="card-value description">{item.description || '-'}</span></div>
                <div className="card-row"><span className="card-label">🏷️ الفئة:</span><span className="card-value"><span className="category-badge">{getCategoryIcon(item.categoury)} {item.categoury}</span></span></div>
                <div className="card-row"><span className="card-label">💳 طريقة الدفع:</span><span className="card-value"><span className="payment-badge">{getPaymentIcon(item.paymentmethod)} {item.paymentmethod}</span></span></div>
                <div className="card-row amount-row"><span className="card-label">💰 المبلغ:</span><span className={`card-value amount-value ${item.type === 'Expense' ? 'expense-amount' : 'deposit-amount'}`}>{item.money.toLocaleString()} درهم</span></div>
              </div>
              <div className="card-actions">
                {activeTab === 'active' && (<><button onClick={() => handleEdit(item)} className="card-btn-edit" title="تعديل">✏️ تعديل</button><button onClick={() => handleDelete(item._id)} className="card-btn-delete" title="حذف">🗑️ حذف</button></>)}
                {activeTab === 'deleted' && (<button onClick={() => handleRestore(item._id)} className="card-btn-restore" title="استعادة">↩️ استعادة</button>)}
                {activeTab === 'all' && (<div className="card-actions-all">{item.isActive ? <button onClick={() => handleDelete(item._id)} className="card-btn-delete" title="حذف">🗑️ حذف</button> : <button onClick={() => handleRestore(item._id)} className="card-btn-restore" title="استعادة">↩️ استعادة</button>}<button onClick={() => handleEdit(item)} className="card-btn-edit" title="تعديل">✏️ تعديل</button></div>)}
              </div>
            </div>
          ))
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">عرض {(pagination.currentPage - 1) * pagination.limit + 1} - {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} من {pagination.totalCount} معاملة</div>
          <div className="pagination-controls">
            <button onClick={() => handlePageChange(1)} disabled={pagination.currentPage === 1} className="pagination-btn">« الأول</button>
            <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} className="pagination-btn">‹ السابق</button>
            {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
              let pageNum;
              if (pagination.totalPages <= 5) pageNum = i + 1;
              else if (pagination.currentPage <= 3) pageNum = i + 1;
              else if (pagination.currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
              else pageNum = pagination.currentPage - 2 + i;
              return (<button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`pagination-btn ${pagination.currentPage === pageNum ? 'active' : ''}`}>{pageNum}</button>);
            })}
            <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages} className="pagination-btn">التالي ›</button>
            <button onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages} className="pagination-btn">الأخير »</button>
          </div>
        </div>
      )}

      {showModal && activeTab === 'active' && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{editingItem ? '✏️ تعديل المعاملة' : '➕ إضافة معاملة جديدة'}</h2><button className="close-btn" onClick={() => { setShowModal(false); resetForm(); }}>✕</button></div>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>📝 الوصف</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="أدخل وصفاً مفصلاً للمعاملة..." rows="4" /></div>
              <div className="form-row"><div className="form-group"><label>💰 النوع</label><select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}><option value="Expense">📉 مصروف</option><option value="Deposite">📈 إيداع</option></select></div><div className="form-group"><label>💵 المبلغ</label><input type="number" value={formData.money} onChange={(e) => setFormData({ ...formData, money: e.target.value })} required placeholder="أدخل المبلغ" /></div></div>
              <div className="form-row"><div className="form-group"><label>📅 التاريخ</label><input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required /></div>
              <div className="form-group"><label>🏷️ الفئة</label>
                <select value={formData.categoury} onChange={(e) => setFormData({ ...formData, categoury: e.target.value })}>
                  <option value="Rent">🏠 إيجار</option>
                  <option value="Billing">💡 فواتير</option>
                  {/* ========== التعديل الثالث: إضافة الفئات الجديدة في نموذج الإدخال ========== */}
                  <option value="salary">💰 رواتب</option>
                  <option value="accoumiditio">🪪 اقامات</option>
                  <option value="licience">📜 رخصة</option>
                  <option value="Supplies">📦 مستلزمات</option>
                  <option value="Services">⚙️ خدمات</option>
                  <option value="Traning">📚 تدريب</option>
                  <option value="Travilling">✈️ سفر</option>
                  <option value="Markiting">📢 تسويق</option>
                  <option value="Other">📌 أخرى</option>
                </select>
              </div></div>
              <div className="form-group"><label>💳 طريقة الدفع</label><select value={formData.paymentmethod} onChange={(e) => setFormData({ ...formData, paymentmethod: e.target.value })}><option value="Cash">💵 كاش</option><option value="Bank Transfer">🏦 تحويل بنكي</option><option value="Credit Card">💳 بطاقة ائتمان</option><option value="Check">📝 شيك</option><option value="Other">🔧 أخرى</option></select></div>
              <div className="form-buttons"><button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>إلغاء</button><button type="submit" className="btn-save" disabled={loading}>{loading ? 'جاري الحفظ...' : (editingItem ? 'تحديث' : 'حفظ')}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};